<template>
  <div class="ascii-grass-container">
    <canvas ref="canvas" class="ascii-canvas"></canvas>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const canvas = ref(null)
let ctx = null
let animationId = null
let time = 0
let lastFrameTime = 0
const TARGET_FPS = 24

const CHAR_SIZE = 16
const VERT_SCALE = (CHAR_SIZE * 0.6) | 0

// Blade colors — dark base to bright tips
const BLADE_COLORS = ['#1e4812', '#2d5a1d', '#3d6b1a', '#4a7c23', '#5a8f2a', '#6b9e35', '#7bad40', '#8aba4a']
const BLADE_COLOR_COUNT = BLADE_COLORS.length

// Ground cover colors — dark earthy greens
const GROUND_COLORS = ['#1a3d10', '#1e4812', '#224d14', '#2a5a18', '#264f16']
const GROUND_CHARS = ['.', ',', "'", '`', ';', '"', ':', '*']

const PADDING = 40
const STRIP_H = 6

let offscreen = null
let windStrength = 1
let targetWindStrength = 1
let viewW = 0
let viewH = 0

// Pre-render the entire grass field once to an offscreen canvas (only runs on init/resize)
const preRenderGrass = () => {
  viewW = canvas.value.width
  viewH = canvas.value.height

  const offW = viewW + PADDING * 2
  offscreen = document.createElement('canvas')
  offscreen.width = offW
  offscreen.height = viewH
  const oc = offscreen.getContext('2d')

  // Layer 1: solid dark green base
  oc.fillStyle = '#162e0a'
  oc.fillRect(0, 0, offW, viewH)

  // Layer 2: dense ground cover — small characters filling the surface
  oc.textAlign = 'center'
  oc.textBaseline = 'middle'
  oc.font = '10px monospace'
  for (let gy = 0; gy < viewH; gy += 6) {
    for (let gx = 0; gx < offW; gx += 5) {
      oc.fillStyle = GROUND_COLORS[(Math.random() * GROUND_COLORS.length) | 0]
      oc.fillText(
        GROUND_CHARS[(Math.random() * GROUND_CHARS.length) | 0],
        gx + Math.random() * 3,
        gy + Math.random() * 3
      )
    }
  }

  // Layer 3: short tufts — medium-sized characters for mid-layer fill
  oc.font = '12px monospace'
  const tuftColors = ['#2d5a1d', '#3d6b1a', '#4a7c23', '#325e1c']
  const tuftChars = ['w', 'v', 'n', 'm', '~']
  for (let ty = 0; ty < viewH; ty += 14) {
    for (let tx = 0; tx < offW; tx += 10) {
      if (Math.random() > 0.6) continue
      oc.fillStyle = tuftColors[(Math.random() * tuftColors.length) | 0]
      oc.fillText(
        tuftChars[(Math.random() * tuftChars.length) | 0],
        tx + Math.random() * 6,
        ty + Math.random() * 6
      )
    }
  }

  // Layer 4: tall grass blades
  oc.font = `${CHAR_SIZE}px monospace`
  const spacingX = 10
  const spacingY = 16

  const blades = []
  for (let row = 0; row < viewH + spacingY; row += spacingY) {
    for (let col = 0; col < offW + spacingX; col += spacingX) {
      blades.push({
        x: col + (Math.random() - 0.5) * 8,
        y: row + (Math.random() - 0.5) * 6,
        segs: ((60 + (row / viewH) * 100 + Math.random() * 50) / CHAR_SIZE) | 0,
        lean: (Math.random() - 0.5) * 2.5
      })
    }
  }
  blades.sort((a, b) => a.y - b.y)

  for (const b of blades) {
    for (let i = 0; i < b.segs; i++) {
      const ratio = i / b.segs
      const colorIdx = Math.min((ratio * BLADE_COLOR_COUNT) | 0, BLADE_COLOR_COUNT - 1)
      oc.fillStyle = BLADE_COLORS[colorIdx]
      const offset = b.lean * ratio * 7
      const char = offset > 2 ? '/' : offset < -2 ? '\\' : '|'
      oc.fillText(char, b.x + offset, b.y - i * VERT_SCALE)
    }
  }
}

// Animation: just draw horizontal strips of the pre-rendered image with wave distortion
const animate = (currentTime) => {
  if (!ctx || !canvas.value || !offscreen) return

  const elapsed = currentTime - lastFrameTime
  if (elapsed < 1000 / TARGET_FPS) {
    animationId = requestAnimationFrame(animate)
    return
  }
  lastFrameTime = currentTime

  ctx.fillStyle = '#162e0a'
  ctx.fillRect(0, 0, viewW, viewH)

  if (Math.random() < 0.01) {
    targetWindStrength = 0.5 + Math.random() * 1.5
  }
  windStrength += (targetWindStrength - windStrength) * 0.02

  const t = time * 0.8
  const t2 = time * 0.5
  const wind = windStrength

  for (let y = 0; y < viewH; y += STRIP_H) {
    const h = Math.min(STRIP_H, viewH - y)
    // Two sine waves at different frequencies for a more organic feel
    const shift = (
      Math.sin(t + y * 0.015) * 12 +
      Math.sin(t2 + y * 0.008) * 6
    ) * wind | 0
    ctx.drawImage(
      offscreen,
      PADDING - shift, y,
      viewW, h,
      0, y,
      viewW, h
    )
  }

  time += 0.08
  animationId = requestAnimationFrame(animate)
}

let resizeTimer = null
const handleResize = () => {
  if (!canvas.value) return
  clearTimeout(resizeTimer)
  resizeTimer = setTimeout(() => {
    canvas.value.width = window.innerWidth
    canvas.value.height = window.innerHeight
    preRenderGrass()
  }, 150)
}

onMounted(() => {
  if (canvas.value) {
    canvas.value.width = window.innerWidth
    canvas.value.height = window.innerHeight
    ctx = canvas.value.getContext('2d', { alpha: false })
    preRenderGrass()
    animationId = requestAnimationFrame(animate)
    window.addEventListener('resize', handleResize)
  }
})

onUnmounted(() => {
  if (animationId) {
    cancelAnimationFrame(animationId)
  }
  clearTimeout(resizeTimer)
  window.removeEventListener('resize', handleResize)
})
</script>

<style scoped>
.ascii-grass-container {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.ascii-canvas {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
